import React, { useRef, useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { UnifiedChatWidget, UnifiedChatWidgetHandle } from '@/components/chat/UnifiedChatWidget';
import { useChatbotQueries } from '@/hooks/useChatbotQueries';
import { Loader2 } from 'lucide-react';

/**
 * Unified Chat Page that supports both fullscreen and embedded modes
 */
export function UnifiedChatPage() {
  const { chatId } = useParams<{ chatId: string }>();
  const [searchParams] = useSearchParams();
  const chatWidgetRef = useRef<UnifiedChatWidgetHandle>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Get override values from URL params for preview
  const urlPreviewTitle = searchParams.get('title');
  const urlCompanyName = searchParams.get('companyName');
  const urlCompanyLogo = searchParams.get('companyLogo');
  const urlTheme = searchParams.get('theme');
  const piiToken = searchParams.get('pii');

  const [title, setTitle] = useState<string | null>(
    urlPreviewTitle ? decodeURIComponent(urlPreviewTitle) : null
  );

  // Get embedded mode from URL params
  const embedded = searchParams.get('embedded') === 'true';

  // Parse theme from URL if provided
  useEffect(() => {
    const themeParam = urlTheme; // Use the already retrieved urlTheme
    if (themeParam) {
      try {
        const decodedTheme = decodeURIComponent(themeParam);
        const parsedTheme = JSON.parse(decodedTheme);
        console.log('Parsed theme from URL:', parsedTheme);
      } catch (err) {
        console.error("Error parsing theme from URL:", err);
      }
    }

    // Force loading to complete after a timeout
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, [searchParams]);

  // Use the useChatbotQueries hook with the chatbotId
  const {
    chatbot,
    theme: themeData,
    transformedQuestions,
    transformedTriggerRules,
    isLoading: isLoadingQueries,
  } = useChatbotQueries(chatId);

  // Update loading state when queries complete
  useEffect(() => {
    if (!isLoadingQueries) {
      setIsLoading(false);
    }
  }, [isLoadingQueries]);

  // Update title when chatbot data is loaded, if not overridden by URL
  useEffect(() => {
    if (chatbot && !urlPreviewTitle) { // Only set from chatbot if no URL override
      setTitle(chatbot.title || chatbot.name || 'Chat With Us');
    }
    // If urlPreviewTitle exists, it's already set in useState initialization
  }, [chatbot, urlPreviewTitle]);

  // Handle title change (e.g., if UnifiedChatWidget could change it internally)
  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
  };

  // Combine theme from URL and database
  const finalTheme = {
    ...themeData,
    // Override with URL params if provided
    ...(urlTheme ? JSON.parse(decodeURIComponent(urlTheme)) : {})
  };

  // Extract delay settings from chatbot general settings
  const generalSettings = chatbot?.settings as any || {};
  const messageDelay = generalSettings.messageDelay || 1000;
  const buttonOptionsDelay = generalSettings.buttonOptionsDelay || 0;
  const noResponseDelay = generalSettings.noResponseDelay || 2000;
  const optionsHandlerDelay = generalSettings.optionsHandlerDelay || 10;
  const showTimestamp = generalSettings.showTimestamp ?? true;

  // Show loading state
  if (isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gray-50">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-lg font-medium text-gray-600">Loading chat...</p>
        </div>
      </div>
    );
  }

  // Show error if chatbot not found
  if (!chatbot && !isLoading) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md p-8 bg-white rounded-lg shadow-lg">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Chat Not Found</h1>
          <p className="text-gray-700 mb-4">
            The chat you're looking for doesn't exist or has been removed.
          </p>
          <a
            href="/"
            className="inline-block px-4 py-2 bg-primary text-white rounded-md hover:bg-primary/90"
          >
            Return Home
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      className="h-screen w-screen flex items-center justify-center bg-gray-50"
      data-testid="unified-chat-page"
      data-chatbot-id={chatId}
    >
      <div className={embedded ? "w-full h-full" : "w-full h-full md:w-[500px] md:h-[90vh] md:shadow-2xl md:rounded-2xl overflow-hidden"}>
        <UnifiedChatWidget
          ref={chatWidgetRef}
          theme={finalTheme}
          isFullscreen={true}
          questions={transformedQuestions || []}
          triggerRules={transformedTriggerRules || []}
          welcomeMessage={chatbot?.welcome_message || 'Welcome! How can I help you today?'}
          completionMessage={chatbot?.completion_message || 'Thank you for chatting with us!'}
          chatbotId={chatId}
          title={title || 'Chat With Us'} // title state now respects URL override first
          avatarUrl={chatbot?.operator_avatar_url}
          operatorName={chatbot?.operator_name || 'Support'}
          companyName={urlCompanyName ? decodeURIComponent(urlCompanyName) : (chatbot?.company_name || 'OfferChat.ai')}
          companyLogo={urlCompanyLogo ? decodeURIComponent(urlCompanyLogo) : chatbot?.company_logo}
          onTitleChange={handleTitleChange}
          initialOpen={true}
          className="h-full"
          piiToken={piiToken}
          messageDelay={messageDelay}
          buttonOptionsDelay={buttonOptionsDelay}
          noResponseDelay={noResponseDelay}
          optionsHandlerDelay={optionsHandlerDelay}
          showTimestamp={showTimestamp}
        />
      </div>
    </div>
  );
}

export default UnifiedChatPage;
